import {
  APIFieldAttributes,
  APIFieldType,
  APIDateOnlyFieldAttributes,
  APIDateTimeFieldAttributes,
  APIComponentAttributes,
  APIReferenceAttributes,
  PlainDateISO,
  PlainDateTimeISO,
  APIGlobalField,
} from '@ardoq/api-types';

type Attributes = APIComponentAttributes | APIReferenceAttributes;

// prettier-ignore
export function getFieldValue(field: APIDateOnlyFieldAttributes, attributes: Attributes): PlainDateISO | null;
// prettier-ignore
export function getFieldValue(field: APIDateTimeFieldAttributes, attributes: Attributes): PlainDateTimeISO | null;
// prettier-ignore
export function getFieldValue(field: APIDateTimeFieldAttributes
                                   | APIDateOnlyFieldAttributes, attributes: Attributes): PlainDateISO
                                                                                        | PlainDateTimeISO | null;
// prettier-ignore
export function getFieldValue(field: APIFieldAttributes, attributes: Attributes): unknown;
// prettier-ignore
export function getFieldValue(field: APIFieldAttributes, attributes: Attributes): unknown {
  return attributes[field.name] ?? null;
}

/**
 * Check if an APIFieldAttributes is a date only field.
 * Note: Doesn't check if it's part of a date range which also makes it a date only field.
 */
export const isDateOnlyField = (
  field: APIFieldAttributes
): field is APIDateOnlyFieldAttributes => {
  return field.type === APIFieldType.DATE_ONLY;
};

/**
 * Check if an APIFieldAttributes is a date time field.
 * Note: Doesn't check if it's part of a date range which also makes it a date time field.
 */
export const isDateTimeField = (
  field: APIFieldAttributes
): field is APIDateTimeFieldAttributes => {
  return field.type === APIFieldType.DATE_TIME;
};

/**
 * Our client-level date-ranges use pairs of fields to represent a date range,
 * to start this process we typically need to identify if a field is DATE_ONLY
 * or DATE_TIME.
 */
export const isDateTimeOrDateOnlyField = (
  field: APIFieldAttributes
): field is APIDateTimeFieldAttributes | APIDateOnlyFieldAttributes => {
  return isDateTimeField(field) || isDateOnlyField(field);
};

export const isFileField = (field: APIFieldAttributes) =>
  field.type === APIFieldType.FILE;

export const isNumberField = (field: APIFieldAttributes) =>
  field.type === APIFieldType.NUMBER;

export const isTextField = (field: APIFieldAttributes) =>
  field.type === APIFieldType.TEXT;

export const isURLField = (field: APIFieldAttributes) =>
  field.type === APIFieldType.URL;

export const isListField = (
  field: APIFieldAttributes
): field is APIFieldAttributes & { type: APIFieldType.LIST } =>
  field.type === APIFieldType.LIST;

export const isSelectedMultipleListField = (
  field: APIFieldAttributes
): field is APIFieldAttributes & { type: APIFieldType.SELECT_MULTIPLE_LIST } =>
  field.type === APIFieldType.SELECT_MULTIPLE_LIST;

export const getListFieldOptions = (field: APIFieldAttributes) => {
  if (
    !(isListField(field) || isSelectedMultipleListField(field)) ||
    typeof field.defaultValue !== 'string'
  ) {
    return [];
  }

  return field.defaultValue.split(',');
};

export const isCalculatedField = (
  field: APIGlobalField | APIFieldAttributes
): boolean => field.calculatedFieldSettings?.storedQueryId !== undefined;

export const isLocallyDerivedField = (
  field: APIGlobalField | APIFieldAttributes
): boolean => field.calculatedFieldSettings?.locallyDerived !== undefined;

export const appliesToComponents = (field: APIFieldAttributes) =>
  field.componentType.length > 0;

export const appliesToComponentType = (
  field: APIFieldAttributes,
  componentTypeId: string,
  modelId: string
) => field.model === modelId && field.componentType.includes(componentTypeId);

export const appliesToReferenceType = (
  field: APIFieldAttributes,
  referenceTypeId: string | number,
  modelId: string
) =>
  field.model === modelId &&
  field.referenceType.includes(String(referenceTypeId));
